import { useState, useEffect, useRef, useCallback } from 'react';
import { Platform } from 'react-native';
import type { ChatThreadSource } from '../services/chat/chatThreadSource';
import type { ThreadMessage } from '../services/chat/chatTypes';
import { createReadPointer } from '../services/chat/readPointer';
import { analytics } from '../services/analytics';
import { withDeadline } from '../services/chat/withDeadline';
import { isHubRefusal } from '../services/chat/hubRefusal';
import { classifySendFailure, type SendResult } from '../services/chat/sendFailure';
import { useRewardReactions } from './useRewardReactions';
import { myReactionCalls } from '../services/chat/myReactionCalls';

// Исход отправки объявлен рядом с классификатором; здесь он только
// переэкспортируется, чтобы прежние импорты из хука продолжали работать.
export type { SendResult } from '../services/chat/sendFailure';

/**
 * CORE-396 — wires any {@link ChatThreadSource} (GetStream or Agape.ChatApi)
 * into the state {@link ../components/chat/AgapellaChatThread} renders: message
 * list, "thinking" indicator, loading/error, and a `send`. Owns the source
 * lifecycle (connect on mount / whenever `makeSource` changes, disconnect on
 * cleanup). The backend is entirely behind `makeSource`.
 *
 * `makeSource` is a factory (usually `useCallback`-memoised on its inputs) so a
 * new companion / channel re-runs it; pass `null` to render an idle thread.
 */
// Connecting the source (channel provision REST call + SignalR/Stream socket)
// can fail transiently — a cold CI runner, a flaky WS upgrade, an RU hiccup, or
// a token that lands a beat late. Erroring on the FIRST failure stranded the
// thread on "Не удалось открыть чат" until remount (the flaky AI-chat e2e). Retry
// a few times with backoff before surfacing the error.
const CONNECT_ATTEMPTS = 3;
const CONNECT_BACKOFF_MS = [400, 1200]; // between attempts 1→2 and 2→3

/**
 * Ceiling on one connect attempt, end to end.
 *
 * The retry above only ever helped a REJECTION. An await that never settles —
 * a request without a deadline, a hub invocation the server never completes —
 * walked straight past it: no retry, no `error`, no telemetry, just the spinner
 * forever. Reported from production as "the chat hangs on an endless load after
 * accepting the consent, and a hard reload does not help", for some accounts and
 * not others. Whatever hangs, this turns it into a failure the machinery below
 * already knows how to handle.
 *
 * 20 s clears the legitimate worst case with room to spare: the direct hub gets
 * 10 s before falling back to the RU proxy, which then has its own connect and
 * a WatchChannel round-trip to make.
 */
const CONNECT_ATTEMPT_TIMEOUT_MS = 20_000;

// CORE-1020 — сам приём переехал в `services/chat/withDeadline`: он понадобился
// и внутри канала (WatchChannel), а сервису не следует импортировать хук.
// Имя здесь остаётся тем же, чтобы у прежних вызовов ничего не поменялось.
export { withDeadline } from '../services/chat/withDeadline';

export interface ChatThreadSourceOptions {
  /**
   * CORE-1791 — тред на экране. На телефоне в /chats тред не размонтируется,
   * пока показан список (черновик, прокрутка), — но и отмечать прочитанным то,
   * что пришло, пока человек смотрит список, он не должен: строка потеряла бы
   * счётчик непрочитанного. По умолчанию `true` — для всех прочих мест как было.
   */
  shown?: boolean;
}

export function useChatThreadSource(
  makeSource: (() => Promise<ChatThreadSource | null>) | null,
  options?: ChatThreadSourceOptions,
) {
  const shown = options?.shown ?? true;
  const shownRef = useRef(shown);
  shownRef.current = shown;
  const readPointerRef = useRef<{ schedule(): void } | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [thinking, setThinking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const sourceRef = useRef<ChatThreadSource | null>(null);
  const lastTypingRef = useRef(0);
  // Live view of the messages for callbacks that must read current state without
  // being re-created on every change (see `react`).
  const messagesRef = useRef<ThreadMessage[]>([]);
  messagesRef.current = messages;

  useEffect(() => {
    if (!makeSource) {
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(false);
    setMessages([]);
    setThinking(false);

    // CORE-629 — the read pointer has to follow the thread, not just its
    // opening. markRead() used to fire once after connect(), so anything that
    // landed while the chat stayed open — above all Agapella's reply to the
    // message the user had just sent — was displayed as read but left unread on
    // the server, and the badge kept a phantom "1" through hard refreshes.
    const readPointer = createReadPointer({
      markRead: () => sourceRef.current?.markRead(),
      isVisible: () =>
        shownRef.current
        && (Platform.OS !== 'web' || typeof document === 'undefined' || !document.hidden),
    });
    readPointerRef.current = readPointer;
    // Coming back to a tab that received messages while hidden reads them.
    const onVisible = () => readPointer.schedule();
    const isWeb = Platform.OS === 'web' && typeof window !== 'undefined';
    if (isWeb) {
      window.addEventListener('focus', onVisible);
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisible);
      }
    }

    (async () => {
      for (let attempt = 0; alive && attempt < CONNECT_ATTEMPTS; attempt++) {
        let source: ChatThreadSource | null = null;
        try {
          source = await withDeadline(
            makeSource(), CONNECT_ATTEMPT_TIMEOUT_MS, 'provisioning the channel',
          );
          if (!alive) {
            source?.disconnect();
            return;
          }
          if (!source) {
            // A factory that legitimately has nothing to open (no channel for
            // this surface). Not an error — but it must still end the spinner,
            // which an early `return` here used to skip.
            setError(false);
            setLoading(false);
            return;
          }
          sourceRef.current = source;
          source.onChange(() => {
            if (!alive) return;
            setMessages(source!.getMessages());
            setThinking(source!.isThinking());
            readPointer.schedule();
          });
          await withDeadline(
            source.connect(), CONNECT_ATTEMPT_TIMEOUT_MS, 'opening the channel',
          );
          if (!alive) {
            source.disconnect();
            return;
          }
          // CORE-1791 — скрытый тред отметит прочитанное, когда его покажут.
          if (shownRef.current) source.markRead();
          setError(false);
          setLoading(false);
          return; // connected
        } catch (err) {
          // Tear down the half-open attempt before retrying so we don't leak a
          // connection, then back off. Only the final failure surfaces `error`.
          try { await source?.disconnect(); } catch { /* ignore */ }
          if (sourceRef.current === source) sourceRef.current = null;
          if (!alive) return;
          // CORE-1211 — повтор заведён на осечку связи: холодный раннер, кривой
          // WS-апгрейд, токен, приехавший на такт позже. Отказ хаба в правах к
          // этому списку не относится — он не осечка, а ответ, и на второй и
          // третий раз он будет тот же самый. Показываем экран сразу, а не
          // через 1.6 секунды ожидания и три одинаковые ошибки на сервере.
          if (attempt === CONNECT_ATTEMPTS - 1 || isHubRefusal(err)) {
            setError(true);
            setLoading(false);
            return;
          }
          await new Promise((r) => setTimeout(r, CONNECT_BACKOFF_MS[attempt] ?? 1200));
        }
      }
    })();
    return () => {
      alive = false;
      readPointer.cancel();
      if (readPointerRef.current === readPointer) readPointerRef.current = null;
      if (isWeb) {
        window.removeEventListener('focus', onVisible);
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', onVisible);
        }
      }
      sourceRef.current?.disconnect();
      sourceRef.current = null;
    };
  }, [makeSource]);

  // CORE-1791 — тред снова на экране (вернулись из списка): пришедшее за это
  // время читается сейчас, как при возврате во вкладку.
  useEffect(() => {
    if (shown) readPointerRef.current?.schedule();
  }, [shown]);

  const send = useCallback(
    (
      text: string,
      quotedParentId?: string,
      attachments?: any[],
      replyToMessageId?: string,
      replyToFragment?: string,
      clientMessageId?: string,
    ): Promise<SendResult> => {
      // CORE-1020 — обещание отправки нельзя ронять на пол. На вставшем туннеле
      // (прод, РФ-эдж) оно отклоняется по дедлайну, и без этого `catch` отказ
      // становился необработанным отклонением: в консоли шум, в телеметрии ничего,
      // а человек видел пустой чат и не знал, что произошло.
      //
      // CORE-1350 (П3) — а «в телеметрии есть, человеку ничего» оказалось
      // половиной дела. Тогдашний разбор опирался на то, что сообщение всё равно
      // приедет с историей, «потому что на сервере оно уже есть». Для сорванного
      // по времени вызова это верно, для остальных отказов — нет: при мёртвом
      // сокете `requireConn` отклоняет вызов, ни разу не сходив на сервер, а хаб
      // умеет отвечать «нельзя» (`moderation_blocked`, `muted`). В этих случаях
      // сохранять нечему и приезжать нечему, а поле композер к этому моменту уже
      // очистил — написанное исчезало насовсем.
      //
      // Поэтому исход возвращается наружу. Обещание по-прежнему не отклоняется:
      // необработанных отклонений здесь быть не должно, это и было исходной
      // причиной CORE-1020.
      return Promise.resolve(sourceRef.current?.send(text, quotedParentId, attachments, replyToMessageId, replyToFragment, clientMessageId))
        .then<SendResult>(() => ({ outcome: 'sent' }))
        .catch<SendResult>((err) => {
          const failure = classifySendFailure(err);
          analytics.track('chat_send_failed', {
            reason: failure.reason,
            outcome: failure.outcome,
          });
          return failure;
        });
    },
    [],
  );

  /**
   * One reaction per person per message: picking a second emoji REPLACES the
   * first rather than piling up beside it, and tapping the one already set
   * removes it.
   *
   * The hub only exposes Add/Remove, never «set my reaction», so a switch has to
   * take the previous one off explicitly. Callers pass `add` computed as
   * `myReaction !== type`, which made a switch a bare AddReaction — both emoji
   * then stayed live on the server and one person showed up as 😍 1 👍 1.
   *
   * Signature stays synchronous so no call site has to change; the ordering runs
   * inside. Removal is awaited BEFORE the add — issued together they can land
   * with the removal last, wiping the reaction just set.
   *
   * CORE-1479 (возврат QA 23.10) — `type` здесь слаг чипа, то есть комплекта, а
   * снимать надо тот, что стоит (прежнюю `love` под ладонью): что именно
   * вызывать, решает `myReactionCalls` по всем моим реакциям сообщения.
   */
  // CORE-1479 — наградную реакцию ставит только тот, у кого она открыта.
  const { guardAdd: guardRewardReaction } = useRewardReactions();
  const react = useCallback((messageId: string, type: string, add: boolean) => {
    const source = sourceRef.current;
    if (!source?.react) return;
    if (add && !guardRewardReaction(type)) return;
    void (async () => {
      try {
        const mine = messagesRef.current.find((m) => m.id === messageId)?.myReactions ?? [];
        for (const call of myReactionCalls(mine, type, add)) {
          await source.react!(messageId, call.type, call.add);
        }
      } catch {
        // A reaction that didn't stick is not worth interrupting the thread over;
        // the next hub update re-renders whatever the server actually holds.
      }
    })();
  }, [guardRewardReaction]);

  const edit = useCallback((messageId: string, text: string) => {
    sourceRef.current?.edit?.(messageId, text);
  }, []);

  const remove = useCallback((messageId: string) => {
    sourceRef.current?.remove?.(messageId);
  }, []);

  // CORE-1683 — history around a message the thread wants to jump to but does
  // not have. `false` on a source without the op or when the page is empty.
  const loadAround = useCallback(async (messageId: string): Promise<boolean> => {
    const source = sourceRef.current;
    if (!source?.loadAround) return false;
    try {
      return await source.loadAround(messageId);
    } catch {
      return false;
    }
  }, []);

  // CORE-1683 (QA 16.10) — панель обсуждения ищет свой корень за пределами
  // первой страницы: страница старше, корень по известному сообщению, тред
  // целиком. Все три молчат на источнике без этих операций.
  const loadOlder = useCallback(async (): Promise<boolean> => {
    const source = sourceRef.current;
    if (!source?.loadOlder) return false;
    try { return await source.loadOlder(); } catch { return false; }
  }, []);
  const resolveThreadRoot = useCallback(async (messageId: string): Promise<string | null> => {
    const source = sourceRef.current;
    if (!source?.resolveThreadRoot) return null;
    try { return await source.resolveThreadRoot(messageId); } catch { return null; }
  }, []);
  const loadThread = useCallback(async (rootId: string): Promise<boolean> => {
    const source = sourceRef.current;
    if (!source?.loadThread) return false;
    try { return await source.loadThread(rootId); } catch { return false; }
  }, []);
  /** Сообщения источника прямо сейчас, минуя очередь перерисовки. */
  const peekMessages = useCallback((): ThreadMessage[] => sourceRef.current?.getMessages() ?? [], []);

  // Fire the "I'm typing" signal on keystroke, throttled to ~1/2s so the
  // ChatApi hub / Stream keystroke aren't spammed on every character.
  const notifyTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingRef.current < 2000) return;
    lastTypingRef.current = now;
    sourceRef.current?.notifyTyping?.();
  }, []);

  return {
    messages, thinking, loading, error, send, react, notifyTyping, edit, remove, loadAround,
    loadOlder, resolveThreadRoot, loadThread, peekMessages,
  };
}
